import { useContext, useEffect, useState, useMemo } from 'react'
import { TableContext } from 'Context/Table'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useMasterDataStore } from 'Store/MasterData'
import { useCertificateStore } from 'Store/Certificates'
import { shallow } from 'zustand/shallow'
import { TABS } from '../Config'

import Switch from 'Presentation/Components/Table/Cells/Switch'
import CertificateName from './CertificateNameCell'
import { toast } from 'react-toastify'

const VIEW_INDEX = 9

const ViewModel = () => {
	const navigate = useNavigate()
	const [currentTab, setCurrentTab] = useState(VIEW_INDEX)
	const [disabled, setDisabled] = useState(false)
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const {
		updateSorting,
		updateLoading,
		updateError,
		updatePagination,
		resetState: clearStateTable,
	} = useContext(TableContext)

	const profile = useProfileStore(state => state.profile, shallow)
	const getProfile = useProfileStore(state => state.getProfile, shallow)
	const updateProfile = useProfileStore(state => state.updateProfile, shallow)
	const { data, getAll } = useCertificateStore(
		state => ({
			data: state.data,
			getAll: state.getAll,
		}),
		shallow
	)
	const { certMaster, getAllCertMaster } = useMasterDataStore(
		state => ({
			certMaster: state.certificates,
			getAllCertMaster: state.getCertificats,
		}),
		shallow
	)
	const userCertificates = useMemo(() => {
		const results: any[] = []

		certMaster.forEach((item: any) => {
			const findedIdx = data.findIndex((c: any) => c.taxonomyId === parseInt(item.id))

			if (findedIdx !== -1) {
				results.push({
					...item,
					isActive: data[findedIdx].supplierActive === 1,
				})
			}
		})

		return results
	}, [certMaster, data])

	const setupTable = () => {
		updateError(false)
		updatePagination(false)
		updateSorting('nameTh', true)
	}

	const fetchData = async () => {
		updateLoading(true)
		const { success } = await getAll()
		const { success: successCertMaster } = await getAllCertMaster()

		if (!success || !successCertMaster) {
			updateError(true)
			toast.error('Fetch data failed')
		} else {
			updateError(false)
		}
		updateLoading(false)
	}

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const onCheckCertificateActive = async (id: number, isActive: boolean) => {
		setDisabled(true)
		toast.info('Updating certificate...')

		const body: { [key: string]: any } = {
			[`certificates[0][id]`]: id,
			[`certificates[0][isActive]`]: isActive ? 1 : 0,
		}

		const { success, data: updateData } = await updateProfile(body)
		if (!success) {
			toast.error(updateData)
			setDisabled(false)
			return
		} else {
			toast.success('Update certificate success')
			getProfile().then(() => {
				getAll()
				setDisabled(false)
			})
		}
	}

	const TABLE_CONFIGS = [
		{
			Header: 'ชื่อ',
			accessor: 'nameTh',
			width: 200,
			minWidth: 200,
			Cell: ({ row }: any) => {
				return <CertificateName {...row.original} />
			},
		},
		{
			Header: 'รายละเอียด',
			accessor: 'descriptionTh',
			width: 400,
			minWidth: 400,
			Cell: ({ row }: any) => {
				return (
					<div className="d-flex flex-column">
						<span className="text-kumopack-grey-900 fw-bold">
							{row.original.descriptionTh ?? '-'}
						</span>
						<span className="text-kumopack-grey-600">{row.original.descriptionEn ?? '-'}</span>
					</div>
				)
			},
		},
		{
			Header: 'ใช้งานอยู่',
			accessor: 'isActive',
			width: 50,
			minWidth: 50,
			Cell: ({ row }: any) => {
				return (
					<Switch
						disabled={disabled}
						value={row.original.isActive}
						onChange={value => {
							onCheckCertificateActive(row.original.id, value)
						}}
					/>
				)
			},
			disableSortBy: true,
		},
	]

	useEffect(() => {
		setupTable()
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Unmount Handler
	useEffect(() => {
		return () => {
			clearStateTable()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		disabled,
		companyName: profile?.companyName,
		currentTab,
		setCurrentTab,
		onChangeTab,
		userCertificates,
		TABLE_CONFIGS,
	}
}

export default ViewModel
